import { convertParentData, getEvidence } from "./Flow";
import { getSignificantFigures, getUseSetStates } from "./UserSettings";

/*
Handles all the maths, as well as some helpful functions for dealing with the tables (e.g. retrieving, inserting, etc.)
Interacts with a library called bayesjs, which is a bayesian network library for javascript.
All backend, editing not recommended.
*/

//Returns array of probabilities from a condProb table
export function calcBayesianStatsRecursive(condProb, returnStates) {
  if (returnStates === undefined) {
    returnStates = [];
  }
  for (let i = 0; i < condProb.length; i++) {
    let curKey = Object.keys(condProb[i])[0];
    if (
      condProb[i][curKey] !== undefined &&
      condProb[i][curKey].length > 1 &&
      !(
        typeof condProb[i][curKey] === "string" ||
        condProb[i][curKey] instanceof String
      )
    ) {
      calcBayesianStatsRecursive(condProb[i][curKey], returnStates);
    } else if (Array.isArray(condProb[i][curKey])) {
      calcBayesianStatsRecursive(
        condProb[i][curKey][0][Object.keys(condProb[i][curKey][0])[0]],
        returnStates
      );
    } else {
      returnStates.push(condProb[i][curKey]);
    }
  }
  return returnStates;
}

export function getParentStatsRecursive(parentData, returnStates) {
  let parentDataOld = convertParentData(parentData);
  if (returnStates === undefined) {
    returnStates = [];
  }
  for (let i = 0; i < parentDataOld.length; i++) {
    let curKey = Object.keys(parentDataOld[i])[0];
    if (parentDataOld[i][curKey].length > 1) {
      returnStates.push([]);
      getParentStatsRecursive(parentDataOld[i][curKey], returnStates);
    } else {
      returnStates[returnStates.length - 1].push(
        parseFloat(parentDataOld[i][curKey].toFixed(getSignificantFigures()))
      );
    }
  }
  return returnStates;
}

//Helper function, given a flat array of values and a conditional probability table, returns the table with the values inserted in order of the array
export function insertValues(obj, values) {
  Object.keys(obj).forEach((key) => {
    if (!isNaN(obj[key])) {
      //if value is a number - i.e. to be replaced with new  values
      obj[key] = values.shift();
    }
    if (typeof obj[key] === "object" && obj[key] !== null) {
      insertValues(obj[key], values);
    }
  });
  return obj;
}

//Helper function that finds difference between numbers in each array, returns the index of different item, -1 if no difference
//Second return is whether a state was added (1), or removed (-1) - ignore if not using for state addition/removal
export function arrayDif(arr1, arr2) {
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      if (arr1[i] > arr2[i]) {
        return [i, 1];
      } else {
        return [i, -1];
      }
    }
  }
  return [-1, 0];
}

//Helper function, slices array into chunks of size n
export function arrSlice(arr, n) {
  const res = [];
  for (let i = 0; i < arr.length; i += n) {
    const chunk = arr.slice(i, i + n);
    res.push(chunk);
  }
  return res;
}

//function makeKeyValuePair(dictionary) that changes items from "fuel": {'operational': 0.4, 'damaged': 0.6} to "fuel": [{'operational': 0.4}, {'damaged': 0.6}]
export function makeKeyValuePair(dictionary) {
  let newDict = {};
  let keys = Object.keys(dictionary);
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    let value = dictionary[key];
    let newValues = [];
    let valueKeys = Object.keys(value);
    for (let j = 0; j < valueKeys.length; j++) {
      let newKey = valueKeys[j];
      let newValue = {};
      newValue[newKey] = value[newKey];
      newValues.push(newValue);
    }
    newDict[key] = newValues;
  }
  return newDict;
}

//calculate a dictionary of whens beforehand, create a recursive function to do this:
// for each parent, get all parent states, then get each ordered combination of parent state to possible state.
// then, for each combination, create a when object with the parent states as keys and the possible state as the value
function createWhens(node) {
  let parentDataOld = convertParentData(node.data.parentData);
  // if parentData undefined or empty, return empty array
  if (node.data.parentData === undefined || parentDataOld.length === 0) {
    return [];
  }
  let parentNames = [];
  for (let i = 0; i < parentDataOld.length; i++) {
    parentNames.push(Object.keys(parentDataOld[i])[0]);
  }
  let parentStates = getParentStatesRecursive(parentDataOld, []);
  let returnVals = whenValsRecursive(parentStates, parentNames);

  return returnVals;
}

//for each parent, iterate through, check if theres more parents, if so, set currentUsedVals[currentDepth] to be parentStates[i]
function whenValsRecursive(
  parentStates,
  parentNames,
  currentUsedVals,
  returnVals,
  depth
) {
  if (returnVals === undefined) {
    returnVals = [];
  }
  if (currentUsedVals === undefined) {
    currentUsedVals = {};
  }
  if (depth === undefined) {
    depth = 0;
  }
  for (let i = 0; i < parentStates[depth].length; i++) {
    if (parentStates.length > depth + 1) {
      currentUsedVals[parentNames[depth]] = parentStates[depth][i];
      returnVals = whenValsRecursive(
        parentStates,
        parentNames,
        currentUsedVals,
        returnVals,
        depth + 1
      );
    } else {
      currentUsedVals[parentNames[depth]] = parentStates[depth][i];
      returnVals.push(JSON.parse(JSON.stringify(currentUsedVals)));
    }
  }
  return returnVals;
}

function getParentStatesRecursive(parentDataOld, returnStates) {
  if (returnStates === undefined) {
    returnStates = [];
  }
  for (let i = 0; i < parentDataOld.length; i++) {
    let curKey = Object.keys(parentDataOld[i])[0];
    if (parentDataOld[i][curKey].length > 1) {
      returnStates.push([]);
      getParentStatesRecursive(parentDataOld[i][curKey], returnStates);
    } else {
      returnStates[returnStates.length - 1].push(
        Object.keys(parentDataOld[i])[0]
      );
    }
  }
  return returnStates;
}

export function updateStates(nodes) {
  //for each state in states, add the key to a new array
  // let b = require('bayesjs');
  //copy network object with nodes, states and parents using information from nodes
  let newNetwork = {};
  Object.keys(nodes).forEach((key) => {
    let stateNames = [];
    for (let i = 0; i < nodes[key].data.states.length; i++) {
      stateNames.push(Object.keys(nodes[key].data.states[i])[0]);
    }
    let parentNames = [];
    let parentDataOld = convertParentData(nodes[key].data.parentData);

    for (let i = 0; i < parentDataOld.length; i++) {
      parentNames.push(Object.keys(parentDataOld[i])[0]);
    }
    //for each parent, and for each state, construct a cpt object in the when, then format
    let tempCpt = [];

    //calculate a list of whens beforehand, create a recursive function to do this:
    // for each parent, get all parent states, then get each ordered combination of parent state to possible state.
    // then, for each combination, create a when object with the parent states as keys and the possible state as the value
    let whens = createWhens(nodes[key]);
    let whensIndex = 0;

    if (parentDataOld.length > 0) {
      let condProbValues = calcBayesianStatsRecursive(nodes[key].data.condProb);
      let condProbIndex = 0;
      let a = parentDataOld
        .map((p) => Object.values(p)[0].length)
        .reduce((a, b) => a * b);
      for (let i = 0; i < a; i++) {
        let then = {};
        for (let l = 0; l < stateNames.length; l++) {
          then[stateNames[l]] =
            condProbValues[condProbIndex % condProbValues.length];
          condProbIndex += a;
        }
        condProbIndex += 1;
        let when = whens[whensIndex];
        tempCpt.push({ when, then });
        whensIndex += 1;
      }
    } else {
      tempCpt = {};
      for (let i = 0; i < nodes[key].data.condProb.length; i++) {
        tempCpt[stateNames[i]] = Object.values(nodes[key].data.condProb[i])[0];
      }
    }
    newNetwork[nodes[key].id] = {
      id: nodes[key].id,
      states: stateNames,
      parents: parentNames,
      cpt: tempCpt,
    };
    // console.log("here is the new network", newNetwork);
  });
  let b = require("bayesjs");

  const given = {};
  let useSetStates = getUseSetStates();
  if (useSetStates) {
    for (let i = 0; i < nodes.length; i++) {
      if (getEvidence(nodes[i].data.setStates) !== -1) {
        given[nodes[i].id] = Object.keys(
          nodes[i].data.states[getEvidence(nodes[i].data.setStates)]
        )[0];
      }
    }
  }

  const inf = b.inferAll(newNetwork, given, { force: true });

  return inf;
}
